import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
  Divider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import api from '../../services/api';
import { supabase } from '../../services/supabase';
import { toast } from 'react-toastify';
// import { useNavigate } from 'react-router-dom';

// Import terms dialog components
import TermsDialog from '../onboarding/TermsDialog';
import PrivacyPolicyDialog from '../onboarding/PrivacyPolicyDialog';

interface AccountCreationFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

interface AccountFormData {
  email?: string;
  password?: string;
  [key: string]: unknown;
}

interface AccountCreationStepProps {
  formData: AccountFormData;
  onUpdate: (data: Record<string, unknown>) => void;
  onNext: () => void;
  onError: (error: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

interface PasswordStrengthChecks {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
}

interface PasswordStrengthResult {
  strength: number;
  checks: PasswordStrengthChecks;
}

const AccountCreationStep: React.FC<AccountCreationStepProps> = ({ formData, onUpdate, onNext, onError, isLoading, setIsLoading }) => {
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  // Terms and Privacy Policy state
  const [isTncAccepted, setIsTncAccepted] = useState<boolean>(false);
  const [isTncDialogOpen, setIsTncDialogOpen] = useState<boolean>(false);
  const [isPrivacyPolicyAccepted, setIsPrivacyPolicyAccepted] = useState<boolean>(false);
  const [isPrivacyPolicyDialogOpen, setIsPrivacyPolicyDialogOpen] = useState<boolean>(false);

  const { control, handleSubmit, formState: { errors }, watch } = useForm<AccountCreationFormData>({
    defaultValues: {
      email: (formData.email as string) || '',
      password: (formData.password as string) || '',
      confirmPassword: ''
    }
  });

  const watchedPassword = watch('password');
  const watchedEmail = watch('email');

  // Password strength calculation
  const calculatePasswordStrength = (password: string): PasswordStrengthResult => {
    let strength = 0;
    const checks: PasswordStrengthChecks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    strength = Object.values(checks).filter(Boolean).length;
    return { strength, checks };
  };

  const passwordStrength = calculatePasswordStrength(watchedPassword || '');
  const strengthColors: Array<'error' | 'warning' | 'info' | 'success'> = ['error', 'error', 'warning', 'info', 'success', 'success'];
  const strengthLabels: string[] = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];

  // Terms dialog handlers
  const handleTncDialogOpen = (): void => {
    setIsTncDialogOpen(true);
  };

  const handleTncDialogClose = (): void => {
    setIsTncDialogOpen(false);
  };

  const handlePrivacyPolicyDialogOpen = (): void => {
    setIsPrivacyPolicyDialogOpen(true);
  };

  const handlePrivacyPolicyDialogClose = (): void => {
    setIsPrivacyPolicyDialogOpen(false);
  };

  const onSubmit = async (data: AccountCreationFormData): Promise<void> => {
    // Validate terms acceptance
    if (!isTncAccepted) {
      toast.error('You must accept the terms and conditions to proceed.');
      onError('You must accept the terms and conditions to proceed.');
      return;
    }

    if (!isPrivacyPolicyAccepted) {
      toast.error('You must accept the privacy policy to proceed.');
      onError('You must accept the privacy policy to proceed.');
      return;
    }

    setIsLoading(true);
    onError('');

    try {
      // Step 1: Create Supabase auth account and send verification email
      const { error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/quick-email-verification-success`,
        }
      });

      if (signUpError) {
        throw new Error(signUpError.message);
      }

      // Step 2: Create business account with simple API call
      const response = await api.post('/business/simple-create', {
        email: data.email
      });

      if (!response.data || !response.data.success) {
        throw new Error('Failed to create business account');
      }

      // Extract values from nested response structure
      const { business, staff } = response.data.data;
      const token = response.data.token;

      // Save the access token for future API calls
      if (token) {
        // Clear God Mode session state before new authentication
        sessionStorage.removeItem('impersonation_token');
        sessionStorage.removeItem('impersonated_user');
        sessionStorage.removeItem('godmode_original_user');
        sessionStorage.removeItem('godmode_original_token');
        localStorage.setItem('token', token);
        console.log('Access token saved for future API calls');
      }

      // Update form data with all necessary information
      console.log('Account creation successful - stylistId (staff.id) for Instagram:', staff.id);
      onUpdate({
        email: data.email,
        password: data.password,
        userId: business.id,
        businessId: business.id,
        stylistId: staff.id, // This is the staff.id needed for Instagram
        accessToken: token,
        emailVerificationSent: true
      });

      onNext();
    } catch (error: unknown) {
      console.error('Account creation error:', error);
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      onError(err.response?.data?.message || err.message || 'Failed to create business account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = (): void => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box className="account-creation-step">
      <Typography variant="h5" gutterBottom>
        {t('quickOnboarding.account.title', 'Create Your Business Account')}
      </Typography>

      {/* <Typography variant="body1" color="text.secondary" paragraph>
        {t('quickOnboarding.account.description', 'Set up your salon business account and add yourself as the first staff member')}
      </Typography> */}

      <Divider sx={{ my: 3 }} />

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Email Field */}
        <Controller
          name="email"
          control={control}
          rules={{
            required: t('quickOnboarding.account.emailRequired', 'Email is required'),
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: t('quickOnboarding.account.emailInvalid', 'Please enter a valid email address')
            }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              margin="normal"
              label={t('quickOnboarding.account.email', 'Business Email Address')}
              type="email"
              autoComplete="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
                endAdornment: watchedEmail && (
                  <InputAdornment position="end">
                    {errors.email ? (
                      <Cancel color="error" />
                    ) : (
                      <CheckCircle color="success" />
                    )}
                  </InputAdornment>
                )
              }}
            />
          )}
        />

        {/* Password Field */}
        <Controller
          name="password"
          control={control}
          rules={{
            required: t('quickOnboarding.account.passwordRequired', 'Password is required'),
            minLength: {
              value: 8,
              message: t('quickOnboarding.account.passwordMinLength', 'Password must be at least 8 characters')
            }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              margin="normal"
              label={t('quickOnboarding.account.password', 'Password')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}
        />

        {/* Password Strength Indicator */}
        {watchedPassword && (
          <Box className="password-strength" sx={{ mt: 1, mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t('quickOnboarding.account.passwordStrength', 'Password strength:')} {strengthLabels[passwordStrength.strength]}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(passwordStrength.strength / 5) * 100}
              color={strengthColors[passwordStrength.strength]}
              sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
            />
            {passwordStrength.strength < 3 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('quickOnboarding.account.passwordNeed', 'Need:')} {!passwordStrength.checks.uppercase && 'Uppercase, '}
                {!passwordStrength.checks.lowercase && 'Lowercase, '}
                {!passwordStrength.checks.number && 'Number, '}
                {!passwordStrength.checks.special && 'Special character, '}
                {!passwordStrength.checks.length && '8+ characters'}
              </Typography>
            )}
          </Box>
        )}

        {/* Confirm Password Field */}
        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: t('quickOnboarding.account.confirmPasswordRequired', 'Please confirm your password'),
            validate: (value) =>
              value === watchedPassword || t('quickOnboarding.account.passwordsMismatch', 'Passwords do not match')
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              margin="normal"
              label={t('quickOnboarding.account.confirmPassword', 'Confirm Password')}
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                      aria-label="toggle confirm password visibility"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}
        />

        {/* Business Account Notice */}
        <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
          <Typography variant="body2">
            {t('quickOnboarding.account.businessNotice', 'This will create your business account and add you as the first staff member using this email address.')}
          </Typography>
        </Alert>

        {/* Email Verification Notice */}
        <Alert severity="info" sx={{ mt: 1, mb: 3 }}>
          <Typography variant="body2">
            {t('quickOnboarding.account.emailVerificationNotice', 'A verification email will be sent to your email address. Please check your inbox after completing setup.')}
          </Typography>
        </Alert>

        {/* Terms and Conditions Checkboxes */}
        <Box className="terms-section" sx={{ my: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isTncAccepted}
                onChange={(e) => setIsTncAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('quickOnboarding.account.iAccept', 'I accept the')}{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={handleTncDialogOpen}
                  sx={{ textTransform: 'none', p: 0, verticalAlign: 'baseline' }}
                >
                  {t('quickOnboarding.account.termsAndConditions', 'Terms and Conditions')}
                </Button>
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isPrivacyPolicyAccepted}
                onChange={(e) => setIsPrivacyPolicyAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('quickOnboarding.account.iAccept', 'I accept the')}{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={handlePrivacyPolicyDialogOpen}
                  sx={{ textTransform: 'none', p: 0, verticalAlign: 'baseline' }}
                >
                  {t('quickOnboarding.account.privacyPolicy', 'Privacy Policy')}
                </Button>
              </Typography>
            }
          />
        </Box>

        {/* Submit Button */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading || !isTncAccepted || !isPrivacyPolicyAccepted}
          className="submit-button"
          sx={{ mt: 2, py: 1.5 }}
        >
          {isLoading
            ? t('quickOnboarding.account.creating', 'Creating Business Account...')
            : t('quickOnboarding.account.createAccount', 'Create Business Account')
          }
        </Button>
      </form>

      {/* Already have account */}
      <Box className="login-link-section" sx={{ textAlign: 'center', mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('quickOnboarding.account.alreadyHaveAccount', 'Already have an account?')}{' '}
          <Button variant="text" href="/login" className="login-link" sx={{ textTransform: 'none' }}>
            {t('quickOnboarding.account.signIn', 'Sign In')}
          </Button>
        </Typography>
      </Box>

      {/* Temporary button for testing ProfileSetupComplete */}
      {/* <Box sx={{ textAlign: 'center', mt: 2, p: 2, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
          🚧 TEMPORARY - For Testing Only
        </Typography>
        <Button
          variant="outlined"
          color="warning"
          size="small"
          onClick={() => {
            console.log('🧪 Temporary button clicked - navigating to ProfileSetupComplete');
            // Navigate to QuickOnboarding with step 3 (ProfileSetupComplete)
            navigate('/quick-onboarding', {
              state: {
                initialStep: 3,
                formData: {
                  email: 'test@example.com',
                  displayName: 'Test Stylist',
                  showcases: [{ id: 1 }, { id: 2 }],
                  phoneNumber: '+85212345678'
                }
              }
            });
          }}
          sx={{ textTransform: 'none' }}
        >
          🧪 Open Profile Setup Complete in Onboarding
        </Button>
      </Box> */}

      {/* Terms and Privacy Policy Dialogs */}
      <TermsDialog
        isOpen={isTncDialogOpen}
        onClose={handleTncDialogClose}
        currentLanguage={i18n.language}
        timestamp={Date.now()}
        t={{
          close: t('quickOnboarding.account.close', 'Close'),
          termsAndConditions: t('quickOnboarding.account.termsAndConditions', 'Terms and Conditions')
        }}
      />

      <PrivacyPolicyDialog
        isOpen={isPrivacyPolicyDialogOpen}
        onClose={handlePrivacyPolicyDialogClose}
        currentLanguage={i18n.language}
        timestamp={Date.now()}
        t={{
          close: t('quickOnboarding.account.close', 'Close'),
          privacyPolicy: t('quickOnboarding.account.privacyPolicy', 'Privacy Policy')
        }}
      />


    </Box>
  );
};

export default AccountCreationStep;
